import { ApiError } from '@anm/api';
import { BackgroundVariants } from '@anm/components/Background';
import TitleBlock from '@anm/components/TitleBlock';
import { FC } from 'react';
import { branch, compose, renderComponent } from 'recompose';

import NotificationBlock from '../../../notification/NotificationBlock';
import { SubmitFormFields } from '../../SubmitForm';
import { RecoveryContainer, RecoveryTitleBlock } from '../RecoveryContainer';
import RecoveryForm from '../RecoveryForm';

import withRecoveryRequestHandler, {
  RecoveryRequestHandler
} from './HOCs/withRecoveryRequestHandler';

export type RecoverySectionProps = RecoveryRequestHandler & {
  email: string;
  title: string;
  subtitle: string;
  isFormSent: boolean;
  backgroundVariant: BackgroundVariants;
};

type RecoveryNotificationBlockProps = { email: string };

type RecoveryFormContainerProps = {
  title: string;
  error: ApiError | null;
  subtitle: string;
  onRecoveryRequestSubmit: (fields: SubmitFormFields) => void;
};

const RecoveryNotificationBlock: FC<RecoveryNotificationBlockProps> = ({
  email
}) => (
  <NotificationBlock
    icon="images/icons/notification/confirm-email-success.svg"
    title="Password Recovery Request"
    description={` Password recovery information was sent to <br /> <b>${email}</b>`}
  />
);

const UserInactiveError = () => (
  <TitleBlock
    title="Your account is disabled"
    description={
      'Please contact us at <a href="mailto:support@wave.video">support@wave.video</a> to restore it.'
    }
  />
);

const RecoveryFormContainer: FC<RecoveryFormContainerProps> = ({
  title,
  subtitle,
  onRecoveryRequestSubmit
}) => (
  <>
    <RecoveryTitleBlock title={title} description={subtitle} />
    <RecoveryForm onSubmit={onRecoveryRequestSubmit} />
  </>
);

const isUserInactive = ({ error }: RecoveryFormContainerProps) =>
  error ? error.code === 403 : false;

const RecoveryFormContainerWithError = branch(
  isUserInactive,
  renderComponent(UserInactiveError)
)(RecoveryFormContainer);

const isFormSent = ({ isFormSent }: RecoverySectionProps) => isFormSent;
const RecoveryNotificationIfFormSent = compose<
  RecoveryFormContainerProps,
  RecoverySectionProps
>(
  withRecoveryRequestHandler,
  branch(isFormSent, renderComponent(RecoveryNotificationBlock))
)(RecoveryFormContainerWithError);

const RecoverySection: FC<RecoverySectionProps> = props => (
  <RecoveryContainer variant={props.backgroundVariant}>
    <RecoveryNotificationIfFormSent {...props} />
  </RecoveryContainer>
);

export default RecoverySection;
